import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPercent, formatRupiahCompact } from '@/lib/format'
import { TODAY, daysBetween } from '@/lib/date'
import { LogoMark } from '@/components/generals/logo'
import { useDashboard } from '@/components/dashboard/dashboard-provider'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { QUARTER_GOALS } from '@/data/metrics'
import { NAV_GROUPS } from '@/constants/navigation'

import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetTitle
} from '@/components/ui/sheet'

interface SidebarNavProps {
    collapsed?: boolean
    onNavigate?: () => void
}

interface BrandProps {
    collapsed?: boolean
}

const isActivePath = (pathname: string, href: string): boolean => (href === '/' ? pathname === '/' : pathname.startsWith(href))

const Brand: FC<BrandProps> = ({ collapsed }): ReactNode => {
    return (
        <Link
            href='/'
            className={cn(
                'flex items-center gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                collapsed && 'justify-center'
            )}
        >
            <LogoMark className='size-9' />

            {!collapsed && (
                <span className='min-w-0'>
                    <span className='block font-heading text-[15px] font-semibold leading-tight text-sidebar-accent-foreground'>
                        Arunika
                    </span>
                    <span className='block text-xs text-sidebar-foreground/70'>
                        Dasbor bisnis langganan
                    </span>
                </span>
            )}

            {collapsed && <span className='sr-only'>Arunika, ke Ringkasan</span>}
        </Link>
    )
}

export const SidebarNav: FC<SidebarNavProps> = ({ collapsed = false, onNavigate }): ReactNode => {
    const { pathname } = useRouter()

    return (
        <nav aria-label='Navigasi utama' className='flex flex-col gap-6'>
            {NAV_GROUPS.map((group) => (
                <div key={group.label}>
                    {collapsed
                        ? <div className='mx-auto mb-2 h-px w-6 bg-sidebar-border' aria-hidden='true' />
                        : (
                            <p className='mb-2 px-3 text-[11px] font-medium tracking-[0.08em] text-sidebar-foreground/50 uppercase'>
                                {group.label}
                            </p>
                        )}

                    <ul className='flex flex-col gap-1'>
                        {group.items.map((item) => {
                            const active = isActivePath(pathname, item.href)
                            const Icon = item.icon
                            const linkClass = cn(
                                'group relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                                collapsed && 'justify-center px-0',
                                active
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground'
                            )
                            const content = (
                                <>
                                    {active && (
                                        <span
                                            className='absolute top-1/2 left-0 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-sidebar-primary'
                                            aria-hidden='true'
                                        />
                                    )}

                                    <Icon
                                        className={cn(
                                            'size-[18px] shrink-0 transition-colors',
                                            active ? 'text-sidebar-primary' : 'text-sidebar-foreground/70 group-hover:text-sidebar-accent-foreground'
                                        )}
                                        aria-hidden='true'
                                    />

                                    <span className={cn(collapsed && 'sr-only')}>
                                        {item.label}
                                    </span>
                                </>
                            )

                            return (
                                <li key={item.href}>
                                    {collapsed
                                        ? (
                                            <Tooltip>
                                                <TooltipTrigger
                                                    render={(
                                                        <Link
                                                            href={item.href}
                                                            className={linkClass}
                                                            aria-current={active ? 'page' : undefined}
                                                            onClick={onNavigate}
                                                        />
                                                    )}
                                                >
                                                    {content}
                                                </TooltipTrigger>

                                                <TooltipContent side='right' sideOffset={10}>
                                                    {item.label}
                                                </TooltipContent>
                                            </Tooltip>
                                        )
                                        : (
                                            <Link
                                                href={item.href}
                                                className={linkClass}
                                                aria-current={active ? 'page' : undefined}
                                                onClick={onNavigate}
                                            >
                                                {content}
                                            </Link>
                                        )}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            ))}
        </nav>
    )
}

const GoalCard: FC<SidebarNavProps> = ({ onNavigate }): ReactNode => {
    const progress = Math.min(100, (QUARTER_GOALS.mrr.current / QUARTER_GOALS.mrr.target) * 100)
    const daysLeft = daysBetween(TODAY, QUARTER_GOALS.endsAt)

    return (
        <Link
            href='/#target'
            onClick={onNavigate}
            className='block rounded-xl border border-sidebar-border bg-sidebar-accent/50 p-3.5 transition-colors outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring'
        >
            <p className='text-xs text-sidebar-foreground/70'>
                Target MRR {QUARTER_GOALS.label}
            </p>

            <p className='mt-1 text-sm font-semibold text-sidebar-accent-foreground tabular-nums'>
                {formatRupiahCompact(QUARTER_GOALS.mrr.current)}
                <span className='font-normal text-sidebar-foreground/70'> dari {formatRupiahCompact(QUARTER_GOALS.mrr.target)}</span>
            </p>

            <div
                className='mt-2.5 h-1.5 overflow-hidden rounded-full bg-sidebar-border'
                role='progressbar'
                aria-label='Progres target MRR'
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress)}
            >
                <div className='h-full rounded-full bg-sidebar-primary' style={{ width: `${progress}%` }} />
            </div>

            <p className='mt-2 text-[11px] text-sidebar-foreground/60'>
                {formatPercent(progress, 0)} tercapai, {daysLeft} hari lagi
            </p>
        </Link>
    )
}

export const Sidebar: FC = (): ReactNode => {
    const { collapsed } = useDashboard()

    return (
        <aside
            className={cn(
                'sticky top-0 hidden h-screen shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out lg:flex',
                collapsed ? 'w-[76px]' : 'w-64'
            )}
        >
            <div className={cn('flex h-16 shrink-0 items-center px-4', collapsed && 'justify-center px-0')}>
                <Brand collapsed={collapsed} />
            </div>

            <div className={cn('no-scrollbar flex-1 overflow-y-auto px-3 py-5', collapsed && 'px-3.5')}>
                <SidebarNav collapsed={collapsed} />
            </div>

            {!collapsed && (
                <div className='shrink-0 p-3'>
                    <GoalCard />
                </div>
            )}
        </aside>
    )
}

export const MobileSidebar: FC = (): ReactNode => {
    const { mobileNavOpen, setMobileNavOpen } = useDashboard()
    const close = (): void => setMobileNavOpen(false)

    return (
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
                side='left'
                showCloseButton={false}
                className='gap-0 border-sidebar-border bg-sidebar p-0 text-sidebar-foreground data-[side=left]:w-[86%] data-[side=left]:max-w-[300px] data-[side=left]:sm:max-w-[300px]'
            >
                <SheetTitle className='sr-only'>
                    Menu navigasi
                </SheetTitle>

                <SheetDescription className='sr-only'>
                    Pilih halaman dasbor yang ingin dibuka.
                </SheetDescription>

                <div className='flex h-16 shrink-0 items-center justify-between px-4'>
                    <Brand />

                    <SheetClose
                        className='inline-flex size-9 items-center justify-center rounded-lg text-sidebar-foreground transition-colors outline-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring'
                        aria-label='Tutup menu'
                    >
                        <X className='size-5' />
                    </SheetClose>
                </div>

                <div className='flex-1 overflow-y-auto px-3 py-5'>
                    <SidebarNav onNavigate={close} />
                </div>

                <div className='shrink-0 p-3'>
                    <GoalCard onNavigate={close} />
                </div>
            </SheetContent>
        </Sheet>
    )
}
